import { z } from "zod";

import { projectRevisionStatusSchema } from "./projects";

// The controlled vocabularies of the single-line diagram (SLD-a, Master Prompt A26),
// copied verbatim from the backend so a value the API can send is never rejected here.
// Sources at commit aec7867:
// backend/app/domain/electrical/sld/models.py (BusKind, SourceKind, FeederKind,
// DeviceKind, LoadKind), .../sld/structure.py (StructuralErrorCode),
// .../sld/checks.py (SldStatus, SldWarningCode).
// sldContract.test.ts pins each list.
//
// The revision status the document carries is the project spine's own enum, reused
// from the projects service rather than written out a second time.

export const busKindSchema = z.enum(["HT_BUS", "PCC", "MCC", "PDB", "DB"]);

export const sourceKindSchema = z.enum(["GRID", "GENERATOR", "UPS", "PV"]);

export const feederKindSchema = z.enum(["FEEDER", "BUSBAR_TRUNKING"]);

export const deviceKindSchema = z.enum(["ACB", "MCCB", "MCB", "FUSE"]);

export const loadKindSchema = z.enum(["MOTOR", "OTHER"]);

/** A saved diagram is VALID or carries warnings; a structural error never saves. */
export const sldStatusSchema = z.enum(["VALID", "WARNING"]);

/** The rules that refuse a save, every one reported at once (A26 (c)). */
export const structuralErrorCodeSchema = z.enum([
  "DUPLICATE_CODE",
  "UNKNOWN_ENDPOINT",
  "WRONG_ENDPOINT_KIND",
  "SELF_LOOP",
  "CYCLE",
  "SECOND_UPSTREAM_FEEDER",
  "LOAD_NOT_CONNECTED",
  "VOLTAGE_MISMATCH_FEEDER",
  "VOLTAGE_MISMATCH_TRANSFORMER",
  "VOLTAGE_MISMATCH_INCOMER",
]);

/** The warnings a saved diagram may still carry (A26 (d)). */
export const sldWarningCodeSchema = z.enum([
  "BUS_NOT_ENERGISED",
  "BUS_EMPTY",
  "SOURCE_NOT_CONNECTED",
]);

export { projectRevisionStatusSchema };

export type BusKind = z.infer<typeof busKindSchema>;
export type SourceKind = z.infer<typeof sourceKindSchema>;
export type FeederKind = z.infer<typeof feederKindSchema>;
export type DeviceKind = z.infer<typeof deviceKindSchema>;
export type LoadKind = z.infer<typeof loadKindSchema>;
export type SldStatus = z.infer<typeof sldStatusSchema>;
export type StructuralErrorCode = z.infer<typeof structuralErrorCodeSchema>;
export type SldWarningCode = z.infer<typeof sldWarningCodeSchema>;
